import { useCallback, useEffect, useState } from "react";
import {
  Key,
  Loader2,
  CheckCircle2,
  Circle,
  ExternalLink,
  Save,
  Trash2,
  X,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, fmtDateTime, EmptyState } from "./_shared";

export default function AdminIntegrations() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations");
      setCategories(data.categories || []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load integrations.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminLayout
      title="Integrations"
      subtitle="Manage API keys for AI providers and job-board connectors. Saved values take effect immediately for new requests."
    >
      {loading ? (
        <div
          className="flex items-center gap-2 text-slate-500 text-sm"
          data-testid="admin-integrations-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading integrations…
        </div>
      ) : categories.length === 0 ? (
        <EmptyState
          title="Nothing to configure"
          body="The registry is empty."
          testid="admin-integrations-empty"
        />
      ) : (
        <div className="space-y-8" data-testid="admin-integrations-content">
          {categories.map((cat) => (
            <CategoryBlock
              key={cat.key}
              cat={cat}
              onChanged={load}
              data-testid={`admin-integrations-cat-${cat.key}`}
            />
          ))}

          <Card className="p-5" testid="admin-integrations-help">
            <h2 className="text-sm font-medium text-slate-900">
              How these keys are used
            </h2>
            <ul className="mt-3 text-sm text-slate-600 leading-relaxed space-y-2 list-disc pl-5">
              <li>
                <span className="font-medium text-slate-800">Emergent LLM Key</span>{" "}
                covers OpenAI, Anthropic and Gemini through a single subscription.
                Setting OpenAI/Anthropic/Gemini overrides individually is optional.
              </li>
              <li>
                <span className="font-medium text-slate-800">Adzuna</span> is the
                live connector. Seek, Indeed and LinkedIn entries are wired up but
                their connectors return placeholders until those providers issue
                partner credentials.
              </li>
              <li>
                Sensitive values are masked when read back. Audit log entries are
                written on every save and delete.
              </li>
            </ul>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
}

function CategoryBlock({ cat, onChanged }) {
  return (
    <section data-testid={`admin-integrations-cat-${cat.key}`}>
      <header className="mb-3">
        <h2 className="text-lg font-medium text-slate-900">{cat.label}</h2>
        <p className="text-xs text-slate-500">
          {cat.items.filter((i) => i.is_set).length} of {cat.items.length}{" "}
          configured
        </p>
      </header>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {cat.items.map((item) => (
          <IntegrationRow
            key={item.key}
            item={item}
            onChanged={onChanged}
          />
        ))}
      </div>
    </section>
  );
}

function IntegrationRow({ item, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setValue("");
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/integrations/${item.key}`, { value });
      toast.success(`${item.label} saved`);
      setEditing(false);
      setValue("");
      onChanged();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          `Failed to save ${item.label}`,
      );
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/integrations/${item.key}`);
      toast.success(`${item.label} cleared`);
      onChanged();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          `Failed to clear ${item.label}`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5" testid={`admin-integration-${item.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {item.is_set ? (
              <CheckCircle2
                className="h-4 w-4 text-emerald-600 shrink-0"
                aria-label="Configured"
              />
            ) : (
              <Circle
                className="h-4 w-4 text-slate-300 shrink-0"
                aria-label="Not configured"
              />
            )}
            <h3 className="text-sm font-medium text-slate-900 truncate">
              {item.label}
            </h3>
          </div>
          <p className="mt-1 text-xs text-slate-500 leading-relaxed">
            {item.description}
          </p>
        </div>
        {item.link && (
          <a
            href={item.link}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 shrink-0"
            data-testid={`admin-integration-${item.key}-link`}
          >
            Get key <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <dl className="mt-4 grid grid-cols-3 gap-2 text-xs">
        <div className="col-span-3">
          <dt className="text-slate-500 mb-1">Current value</dt>
          <dd
            className="font-mono text-slate-800 rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5 truncate"
            data-testid={`admin-integration-${item.key}-value`}
          >
            {item.masked_value || (
              <span className="italic text-slate-400">— not set —</span>
            )}
          </dd>
        </div>
        {item.updated_at && (
          <div className="col-span-3 text-slate-400">
            Last updated {fmtDateTime(item.updated_at)} by{" "}
            <span className="text-slate-600">{item.updated_by || "system"}</span>
          </div>
        )}
      </dl>

      {!editing ? (
        <div className="mt-4 flex items-center justify-end gap-2">
          {item.is_set && (
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              data-testid={`admin-integration-${item.key}-clear-button`}
            >
              <Trash2 className="h-3 w-3" /> Clear
            </button>
          )}
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
            data-testid={`admin-integration-${item.key}-edit-button`}
          >
            <Key className="h-3 w-3" /> {item.is_set ? "Replace" : "Set value"}
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          <label className="block text-xs font-medium text-slate-700">
            New value
            <div className="mt-1 flex gap-2">
              <input
                type={show || !item.sensitive ? "text" : "password"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={item.placeholder || "Enter value..."}
                className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                data-testid={`admin-integration-${item.key}-input`}
                autoFocus
              />
              {item.sensitive && (
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-slate-500 hover:bg-slate-50"
                  aria-label={show ? "Hide value" : "Show value"}
                  data-testid={`admin-integration-${item.key}-toggle-visibility`}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
          </label>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setValue("");
              }}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              data-testid={`admin-integration-${item.key}-cancel-button`}
            >
              <X className="h-3 w-3" /> Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy || !value.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              data-testid={`admin-integration-${item.key}-save-button`}
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
